import React, { useState, useEffect, useRef } from 'react';
import {
  formatDecimalFieldValue,
  getDecimalInputPattern,
  normalizeDecimalTyping,
  parseDecimalFieldInput,
  roundDecimal,
} from '../../utils/decimalInputUtils';

/**
 * Cantidad como texto (como en guías): acepta coma o punto, hasta 3 o 4 decimales.
 */
const QuantityTextInput = ({
  value = 0,
  onChange,
  disabled = false,
  className = '',
  placeholder = '0',
  decimalPlaces = 3,
  max = null,
  min = null,
  blockNonPositive = false,
}) => {
  const pattern = getDecimalInputPattern(decimalPlaces);
  const [displayValue, setDisplayValue] = useState('');
  const isFocusedRef = useRef(false);

  const clamp = (num) => {
    let n = num;
    if (max != null && Number.isFinite(max) && n > max) n = max;
    if (min != null && Number.isFinite(min) && n < min) n = min;
    if (blockNonPositive && n <= 0) return null;
    return roundDecimal(n, decimalPlaces);
  };

  useEffect(() => {
    if (!isFocusedRef.current) {
      if (value === '' || value === null || value === undefined) {
        setDisplayValue('');
        return;
      }
      const n = parseFloat(value);
      if (!Number.isFinite(n) || n === 0) {
        setDisplayValue(n === 0 ? '0' : '');
        return;
      }
      setDisplayValue(formatDecimalFieldValue(n, pattern));
    }
  }, [value, decimalPlaces]);

  const emitChange = (raw) => {
    const parsed = parseDecimalFieldInput(raw, pattern);
    if (parsed.empty) {
      onChange(0);
      setDisplayValue('');
      return;
    }
    if (parsed.invalid) {
      setDisplayValue(formatDecimalFieldValue(value, pattern));
      return;
    }
    const clamped = clamp(parsed.value);
    if (clamped === null) {
      setDisplayValue(formatDecimalFieldValue(value, pattern));
      return;
    }
    onChange(clamped);
    setDisplayValue(formatDecimalFieldValue(clamped, pattern));
  };

  const handleChange = (e) => {
    const raw = normalizeDecimalTyping(e.target.value);
    if (raw === '') {
      setDisplayValue('');
      onChange(0);
      return;
    }
    if (!pattern.test(raw)) return;
    const parsed = parseFloat(raw);
    if (max != null && Number.isFinite(max) && parsed > max) return;
    if (blockNonPositive && parsed <= 0) return;
    setDisplayValue(raw);
    if (!Number.isNaN(parsed)) {
      onChange(roundDecimal(parsed, decimalPlaces));
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    emitChange(displayValue);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
    />
  );
};

export default QuantityTextInput;
